import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Col, Form, Row } from 'antd';
import { useHistory } from 'react-router-dom';

import { cssVariables } from '../../styles/cssVariables';
import { pxToNumber } from '../../utils/cssUtils';
import { InvoiceDateRange } from '../../widgets/invoice/InvoiceDateRange';
import { InnerContentScrollbars } from '../../components/layout/InnerContentScrollbars';
import { NoLabelForm } from '../../components/forms/forms';
import { FormItemInputText } from '../../components/forms/basicFormElements';
import { FormItemSelect } from '../../components/forms/selects';
import { useAccountOptions } from '../../components/domainSpecific/accountElements';
import { InvoicePaths } from '../../app/enums/InvoicePaths';
import InvoiceContainer from './InvoiceContainer';
import { useInvoiceStyles } from './invoice.styles';

export default function InvoiceSearchForm({ onClose }) {
  const [form] = Form.useForm();
  const history = useHistory();
  const [selectedAccount, setSelectedAccount] = useState();
  const { styles } = useInvoiceStyles();

  const { options: accountOptions = [] } = useAccountOptions({
    filter: acc => !acc?.partner,
  });

  useEffect(() => {
    if (accountOptions.length === 1 && !selectedAccount) {
      setSelectedAccount(accountOptions[0].value);
    }
  }, [accountOptions, selectedAccount]);

  const handleInvoiceFind = () => {
    const formValues = form.getFieldsValue();
    const currentInvoiceNumber = formValues.invoiceNumber?.trim();

    if (currentInvoiceNumber) {
      const searchParams = {
        invoiceNumber: currentInvoiceNumber,
      };
      history.push(InvoicePaths.DETAIL, searchParams);
    }
  };

  const handleJobFind = () => {
    const formValues = form.getFieldsValue();
    const currentJobNumber = formValues.jobNumber?.trim();

    if (currentJobNumber) {
      const searchParams = {
        jobNumber: currentJobNumber,
      };
      history.push(InvoicePaths.DETAIL, searchParams);
    }
  };

  const handleDateRangeSearch = _ => {
    const formValues = form.getFieldValue();
    const { dateStart, dateEnd } = formValues;

    if (selectedAccount && dateStart && dateEnd) {
      const searchParams = {
        id: selectedAccount,
        rangeStart: dateStart.format('YYYY/MM/DD'),
        rangeEnd: dateEnd.format('YYYY/MM/DD'),
      };

      history.push(InvoicePaths.DATE_RESULTS, searchParams);
    }
  };

  return (
    <InnerContentScrollbars>
      <InvoiceContainer titleId="invoice.invoice-lookup" onClose={onClose}>
        <div className={styles.label}>
          <FormattedMessage id="invoice.search" />
        </div>

        <Row align="middle" gutter={pxToNumber(cssVariables.spaceNorm)}>
          <Col flex={1}>
            <NoLabelForm form={form}>
              {/* Invoice Number Search */}
              <Row align="middle" gutter={pxToNumber(cssVariables.spaceNorm)}>
                <Col flex={1}>
                  <FormItemInputText
                    name="invoiceNumber"
                    labelId="invoice.invoiceNumber"
                  />
                </Col>
                <Col style={{ marginBottom: '10px' }}>
                  <Button
                    className="width-80"
                    size="large"
                    type="default"
                    onClick={handleInvoiceFind}
                  >
                    <FormattedMessage id="invoice.find" />
                  </Button>
                </Col>
              </Row>

              {/* Job Number Search */}
              <Row align="middle" gutter={pxToNumber(cssVariables.spaceNorm)}>
                <Col flex={1}>
                  <FormItemInputText
                    name="jobNumber"
                    labelId="invoice.jobNumber"
                  />
                </Col>
                <Col style={{ marginBottom: '10px' }}>
                  <Button
                    className="width-80"
                    size="large"
                    type="default"
                    onClick={handleJobFind}
                  >
                    <FormattedMessage id="invoice.find" />
                  </Button>
                </Col>
              </Row>
            </NoLabelForm>
          </Col>
        </Row>

        <div className={styles.label}>
          <FormattedMessage id="invoice.account" />
        </div>

        <Row align="middle">
          {!selectedAccount ? (
            <>
              <Col flex={1}>
                <NoLabelForm
                  onValuesChange={val => {
                    setSelectedAccount(val?.account);
                  }}
                >
                  <FormItemSelect
                    name="account"
                    options={accountOptions}
                    optionKey="number"
                    labelId="invoice.label"
                  />
                </NoLabelForm>
              </Col>
            </>
          ) : (
            <div
              className={styles.selectedAccount}
              data-testid="selected-account"
            >
              {selectedAccount?.number} | {selectedAccount?.name}
            </div>
          )}
        </Row>

        {/* Date Range Search */}
        <div
          className={!selectedAccount ? styles.disabledSearchWrapper : ''}
          data-testid="date-range-search-wrapper"
          data-disabled={!selectedAccount}
        >
          <Row align="middle" gutter={pxToNumber(cssVariables.spaceNorm)}>
            <Col flex={1}>
              <NoLabelForm form={form}>
                <Row align="middle" gutter={pxToNumber(cssVariables.spaceNorm)}>
                  <Col flex={1}>
                    <InvoiceDateRange
                      form={form}
                      onDateRangeSearch={handleDateRangeSearch}
                      accountConfirmed={!!selectedAccount}
                    />
                  </Col>
                </Row>
              </NoLabelForm>
            </Col>
          </Row>
        </div>
      </InvoiceContainer>
    </InnerContentScrollbars>
  );
}
